export interface SupabaseEdgeFetchConfig {
  /** Full edge-function root URL (no trailing slash).
   *  Local:  `/supabase-functions/root`
   *  Remote: `https://xxx.supabase.co/functions/v1/root`
   */
  baseUrl: string;
  /** Resolves the Auth0/Supabase access token used as the Bearer credential. */
  getToken: () => Promise<string>;
  /**
   * Asked once when a call comes back 401, for a token newer than the one sent. A new
   * token, from here or from getToken when another call already stored it, sends the
   * call again. A long upload batch outlived its token on staging, and one file failed
   * with Authorization required (CLOUD-1089).
   */
  refreshToken?: () => Promise<string | null>;
  /** Supabase region to run the function in, such as `us-east-1`. Empty runs it next to the caller. */
  region?: string;
}

export interface SupabaseEdgeRequestOptions {
  method?: string;
  body?: unknown;
}

/** Error thrown for a non-2xx edge response; carries the status and the optional machine `code`. */
export class EdgeFetchError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'EdgeFetchError';
  }
}

/**
 * Build a typed fetch helper for Supabase Edge Functions.
 *
 * `baseUrl` must already point at the function root (the value returned by
 * `getAuthBackendUrl()`). Endpoint segments are appended directly:
 * `${baseUrl}/${endpoint}`, plus `forceFunctionRegion` when `region` is set. A query,
 * not the `x-region` header, because the functions' CORS rules do not allow that header.
 * Both cookie sessions (via `credentials: 'include'`)
 * and token sessions (via `Authorization: Bearer`) are supported.
 *
 * @example
 * ```ts
 * const supabaseEdgeFetch = createSupabaseEdgeFetch({
 *   baseUrl: getAuthBackendUrl(),
 *   getToken: () => getAccessToken(),
 * });
 *
 * const { connectors } = await supabaseEdgeFetch<ConnectorsListResponse>(
 *   'connectors'
 * );
 * ```
 */
export function createSupabaseEdgeFetch(config: SupabaseEdgeFetchConfig) {
  const region = config.region ? `forceFunctionRegion=${encodeURIComponent(config.region)}` : '';
  return async function supabaseEdgeFetch<T>(endpoint: string, options?: SupabaseEdgeRequestOptions): Promise<T> {
    const glue = endpoint.includes('?') ? '&' : '?';
    const send = (token: string) =>
      fetch(`${config.baseUrl}/${endpoint}${region ? glue + region : ''}`, {
        method: options?.method ?? 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });
    const sent = await config.getToken();
    let response = await send(sent);
    if (response.status === 401 && config.refreshToken) {
      // Calls that fail with the same token at once share one refresh. Only the first gets
      // the newer token back, the others get null, so each one then takes the token now held.
      const fresh = (await config.refreshToken().catch(() => null)) ?? (await config.getToken().catch(() => sent));
      if (fresh && fresh !== sent) {
        // The token is meant to be refreshed before it runs out, so this says the refresh did not happen.
        console.warn(
          `[@nexus/ari-lib] ${endpoint.split('?')[0]} was refused with an expired token, sent again with a newer one`
        );
        response = await send(fresh);
      }
    }

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string; code?: string };
      throw new EdgeFetchError(body.error || `HTTP ${response.status}`, response.status, body.code);
    }

    return response.json() as Promise<T>;
  };
}

export type SupabaseEdgeFetch = ReturnType<typeof createSupabaseEdgeFetch>;
